import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Loader2, RefreshCw, CreditCard, Pencil, Building2, FileText, Banknote, Smartphone, Ticket, User, Wallet } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { numberToWords, formatRawDateTime } from '../../../utils/format';
import { api, couponService, ocrService, unitNomineeService } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import { API_ENDPOINTS } from '../../../config/api';
import { storageEnv } from '../../../config/firebase';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import type { Coupon, OrderUnit } from '../../../types';
import Snackbar from '../../common/Snackbar';
import Select from '../../common/Select';
import MaskedMobile from '../../common/MaskedMobile';
import EditTransactionModal from '../Orders/EditTransactionModal';
import { ImagePreviewModal } from './CreateOrderModal';
import {
    PaymentMethodType,
    MultipleSubMode,
    TransferMode,
    UploadState, emptyUpload,
    uploadToFirebase,
    validateUTR, validateBankName, validateAccountHolderName, validateChequeNumber,
    applyAutoFillValues, clearAutoFilledFields, extractTextFromExistingImage,
    PaymentAutoFillValues, isValidDdmmyyyy, fieldsForPaymentType, transferModeLabel,
} from './paymentFormUtils';

const fmt = (n: any) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '₹0';
    return `₹${Number(n).toLocaleString('en-IN')}`;
};

const methodLabel = (m: string) => m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Same chip set/order as Create New Order's Payment Details section, plus Coupon and the
// MULTIPLE sub-mode chips Update Payment alone supports.
const PAYMENT_METHOD_OPTIONS: { key: PaymentMethodType; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={15} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={15} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={15} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={15} /> },
    { key: 'COUPON', label: 'Coupon', icon: <Ticket size={15} /> },
];
const MULTIPLE_SUB_OPTIONS: { key: MultipleSubMode; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={13} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={13} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={13} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={13} /> },
    { key: 'COUPON', label: 'Coupon', icon: <Ticket size={13} /> },
];

const UserAddPaymentPage: React.FC = () => {
    const { mobile, orderId } = useParams<{ mobile: string; orderId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const _perms = usePermissions();
    const canUpdateOrder = _perms.canAction('users', 'update_order', true);
    const canEditOrderUsers = _perms.canAction('users', 'edit_order', true);
    const canApplyCouponPerm = _perms.canAction('coupons', 'apply_coupons', true);
    const canRemoveCouponPerm = _perms.canAction('coupons', 'remove_coupons', true);
    const [utrDuplicate, setUtrDuplicate] = useState(false);

    const [units, setUnits] = useState<OrderUnit[]>([]);
    const [loadingUnits, setLoadingUnits] = useState(false);
    // Which of the two Update Payment options is showing — defaults to whatever the caller
    // asked for via route state (e.g. the Employee mobile-lookup's per-order "Update Payment"/
    // "Update Nominee" buttons), else null until the order finishes loading (see the
    // effectiveSection fallback below, once `remaining` is known).
    const [activeSection, setActiveSection] = useState<'payment' | 'nominee' | null>(() => {
        const openSection = (location.state as any)?.openSection;
        return openSection === 'payment' || openSection === 'nominee' ? openSection : null;
    });

    const adminMobile = useAppSelector((state: RootState) => state.auth.adminMobile || state.users.adminProfile?.mobile || '');
    const adminName = useAppSelector((state: RootState) => state.auth.adminName || state.users.adminProfile?.first_name || '');
    const adminRole = useAppSelector((state: RootState) => state.users.adminProfile?.role || state.auth.adminRole || '');

    // Role flags (mirror OrderDetailsPage) — drive who may edit a transaction.
    const userRoles = adminRole ? adminRole.split(',').map((r: string) => r.trim()) : [];
    const isSuperAdmin = userRoles.includes('SuperAdmin');
    const isAdmin = userRoles.some((r: string) => r === 'Admin' || r === 'Animalkart admin');
    const isEmployee = userRoles.some((r: string) => r.toLowerCase() === 'employee') || adminRole.toLowerCase().includes('employee');

    const userName: string = (location.state as any)?.userName || '';
    const userCountryCode: string | undefined = (location.state as any)?.countryCode;
    const userId: string | undefined = (location.state as any)?.userId;
    // Where the admin actually came from — falls back to Pending Orders since that's the
    // only caller that doesn't pass this explicitly (yet).
    const fromPage: string = (location.state as any)?.fromPage || 'Pending Orders';

    // Order data
    const [order, setOrder] = useState<any>(null);
    const [orderTotal, setOrderTotal] = useState<number>((location.state as any)?.orderTotal || 0);
    const [numUnits, setNumUnits] = useState<number>((location.state as any)?.numUnits || 0);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [paid, setPaid] = useState<number>(0);
    const [remaining, setRemaining] = useState<number>((location.state as any)?.orderTotal || 0);
    const [loadingOrder, setLoadingOrder] = useState(true);

    // Payment form state
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>('BANK_TRANSFER');
    const [txAmount, setTxAmount] = useState<string | number>('');
    const [txDate, setTxDate] = useState('');
    const [transferMode, setTransferMode] = useState<TransferMode>('NEFT');
    const [paidBy, setPaidBy] = useState('');
    const [utrNumber, setUtrNumber] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [bankName, setBankName] = useState('');
    const [cashierName, setCashierName] = useState('');
    const [cashierPhone, setCashierPhone] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [multipleSubMode, setMultipleSubMode] = useState<MultipleSubMode>('BANK_TRANSFER');
    // Extra the customer paid over the amount due — sent as its own SURPLUS transaction
    // alongside the real payment, so it can be credited to their Animalkart wallet.
    const [surplusAmount, setSurplusAmount] = useState('');
    const [surplusComment, setSurplusComment] = useState('');
    const [comment, setComment] = useState('');

    // Coins state
    const [availableCoins, setAvailableCoins] = useState<number | null>(null);
    const [loadingCoins, setLoadingCoins] = useState(false);
    const [coinsToRedeem, setCoinsToRedeem] = useState<string>('');

    // Wallet state
    const [availableWallet, setAvailableWallet] = useState<number | null>(null);
    const [loadingWallet, setLoadingWallet] = useState(false);
    const [walletAmountToUse, setWalletAmountToUse] = useState<string>('');

    // Upload states
    const [screenshotUpload, setScreenshotUpload] = useState<UploadState>(emptyUpload());
    const [voucherUpload, setVoucherUpload] = useState<UploadState>(emptyUpload());
    const [chequeFrontUpload, setChequeFrontUpload] = useState<UploadState>(emptyUpload());
    const [chequeBackUpload, setChequeBackUpload] = useState<UploadState>(emptyUpload());
    const [upiScreenshotUpload, setUpiScreenshotUpload] = useState<UploadState>(emptyUpload());

    // Fields explicitly Verified via the image preview — only these lock on the payment screen.
    const [verifiedFields, setVerifiedFields] = useState<Set<keyof PaymentAutoFillValues>>(new Set());

    // Plain zoomed-in look at an already-submitted transaction's proof thumbnail (Previous
    // Payments history) — the active payment method's own proof is shown inline above instead.
    const [previewUrl, setPreviewUrl] = useState<{ url: string; plain?: boolean } | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [editingTx, setEditingTx] = useState<any>(null);

    // Coupon block
    const [couponBlock, setCouponBlock] = useState<any>(null);
    const [applying, setApplying] = useState(false);

    const clearErr = (key: string) => setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    // A field was edited (not explicitly Verified) — its previous Verified state and any
    // stale error from a prior failed submit no longer apply to the new value.
    const unverify = (key: string) => setVerifiedFields(prev => {
        if (!prev.has(key as keyof PaymentAutoFillValues)) return prev;
        const next = new Set(prev); next.delete(key as keyof PaymentAutoFillValues); return next;
    });
    const onFieldEdited = (key: keyof PaymentAutoFillValues) => { clearErr(key); unverify(key); };

    // Clears every payment-method-specific field — called whenever the payment method or
    // MULTIPLE sub-mode changes so details from the previous selection never carry over.
    const resetPaymentSubFields = () => {
        setTransferMode('NEFT');
        setUtrNumber('');
        setAccountHolderName('');
        setBankName('');
        setCashierName('');
        setCashierPhone('');
        setPaidBy('');
        setCouponCode('');
        setCoinsToRedeem('');
        setWalletAmountToUse('');
        setSurplusAmount('');
        setSurplusComment('');
        setComment('');
        setScreenshotUpload(emptyUpload());
        setVoucherUpload(emptyUpload());
        setChequeFrontUpload(emptyUpload());
        setChequeBackUpload(emptyUpload());
        setUpiScreenshotUpload(emptyUpload());
        setVerifiedFields(new Set());
        setErrors({});
    };

    const anyUploading = screenshotUpload.uploading || voucherUpload.uploading ||
        chequeFrontUpload.uploading || chequeBackUpload.uploading || upiScreenshotUpload.uploading;

    // Cap the redemption at both the order's remaining balance and the wallet
    // balance (1 coin = ₹1) so we never submit more coins than the investor holds
    // — the backend rejects an over-redemption with a 400.
    const effectiveCoinsRedeemed = paymentMethod === 'COINS_REDEEM'
        ? Math.min(Number(coinsToRedeem) || 0, remaining, availableCoins ?? Infinity)
        : 0;
    const effectiveWalletUsed = paymentMethod === 'WALLET'
        ? Math.min(Number(walletAmountToUse) || 0, remaining, availableWallet ?? Infinity)
        : 0;
    const remainingAfterCoins = Math.max(remaining - effectiveCoinsRedeemed - effectiveWalletUsed, 0);
    const coinsFullyCover = paymentMethod === 'COINS_REDEEM' && remaining > 0 && remainingAfterCoins === 0;
    const isCouponMode = paymentMethod === 'COUPON' || (paymentMethod === 'MULTIPLE' && multipleSubMode === 'COUPON');
    // Gates Add Payment: every field the Extracted Details panel shows for the active mode
    // must be Verified before submitting — mirrors Edit Transaction's same requirement.
    const isRealPaymentMode = !!paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' && !isCouponMode;
    // Whichever upload slot matches the currently active method (or MULTIPLE sub-mode) — the
    // single "proof image" the inline preview panel shows, mirroring Create New Order.
    const activeMode = paymentMethod === 'MULTIPLE' ? multipleSubMode : paymentMethod;
    const isCashDeposit = activeMode === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT';
    // Cash Deposit shows Paid By/UTR/Bank Name too, but they're optional, so they're excluded here.
    const optionalForCashDeposit = ['paidBy', 'utrNumber', 'accountHolderName', 'bankName'];
    const requiredFieldKeys: (keyof PaymentAutoFillValues)[] = isRealPaymentMode
        ? (['txAmount', 'txDate', ...fieldsForPaymentType(activeMode || '', paidBy).map(f => f.key)] as (keyof PaymentAutoFillValues)[])
            .filter(k => !(isCashDeposit && optionalForCashDeposit.includes(k)))
        : [];
    const allFieldsVerified = !isRealPaymentMode || requiredFieldKeys.every(k => verifiedFields.has(k));
    const activeUpload = activeMode === 'CASH' ? voucherUpload
        : activeMode === 'CHEQUE' ? chequeFrontUpload
            : activeMode === 'UPI' ? upiScreenshotUpload
                : screenshotUpload;
    const setActiveUpload = activeMode === 'CASH' ? setVoucherUpload
        : activeMode === 'CHEQUE' ? setChequeFrontUpload
            : activeMode === 'UPI' ? setUpiScreenshotUpload
                : setScreenshotUpload;
    const activeUploadErrorKey = activeMode === 'CASH' ? 'voucherUpload'
        : activeMode === 'CHEQUE' ? 'chequeFrontUpload'
            : activeMode === 'UPI' ? 'upiScreenshotUpload'
                : 'screenshotUpload';
    const activeUploadLabel = activeMode === 'CASH' ? 'Voucher Image'
        : activeMode === 'CHEQUE' ? 'Cheque Front Image'
            : activeMode === 'UPI' ? 'Payment Screenshot'
                : isCashDeposit ? 'Deposit Slip / Screenshot'
                    : 'Payment Document';

    // OCR text extracted from whichever upload is "active" — drives the inline
    // image+OCR+fields panel below (same pattern as Create New Order).
    const [ocrText, setOcrText] = useState<string | null>(null);
    const [ocrLoading, setOcrLoading] = useState(false);
    useEffect(() => {
        const url = activeUpload.url;
        if (!url) { setOcrText(null); setOcrLoading(false); return; }
        let cancelled = false;
        setOcrLoading(true);
        const run = activeUpload.file
            ? ocrService.extractText(activeUpload.file).then(r => r?.text || null)
            : extractTextFromExistingImage(url);
        run
            .then(text => { if (!cancelled) setOcrText(text); })
            .catch(() => { if (!cancelled) setOcrText(null); })
            .finally(() => { if (!cancelled) setOcrLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUpload.url, activeUpload.file]);

    // Coupons assigned to this customer — fetched only while the Coupon mode is active.
    const [userCoupons, setUserCoupons] = useState<Coupon[]>([]);
    const [loadingUserCoupons, setLoadingUserCoupons] = useState(false);
    useEffect(() => {
        if (!isCouponMode || !mobile) { setUserCoupons([]); return; }
        setLoadingUserCoupons(true);
        couponService.getUserCoupons(mobile, 'ASSIGNED')
            .then(setUserCoupons)
            .finally(() => setLoadingUserCoupons(false));
    }, [isCouponMode, mobile]);

    const fetchAvailableCoins = useCallback(async (mob: string) => {
        setLoadingCoins(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getCoinTransaction(mob));
            if (response?.data?.statuscode === 200 && response?.data?.stats) {
                setAvailableCoins(Math.floor(response.data.stats.remaining_coins ?? 0));
            } else {
                setAvailableCoins(0);
            }
        } catch { setAvailableCoins(0); } finally { setLoadingCoins(false); }
    }, []);

    const fetchAvailableWallet = useCallback(async (mob: string) => {
        setLoadingWallet(true);
        try {
            const response = await api.get<any>(API_ENDPOINTS.getMoneyTransaction(mob));
            if (response?.data?.statuscode === 200 && response?.data?.stats) {
                setAvailableWallet(Math.floor(response.data.stats.remaining_money ?? 0));
            } else {
                setAvailableWallet(0);
            }
        } catch { setAvailableWallet(0); } finally { setLoadingWallet(false); }
    }, []);

    useEffect(() => {
        if (mobile) {
            fetchAvailableCoins(mobile);
            fetchAvailableWallet(mobile);
        }
    }, [fetchAvailableCoins, fetchAvailableWallet, mobile]);

    const fetchOrder = useCallback(async (): Promise<number | null> => {
        if (!orderId) return null;
        setLoadingOrder(true);
        try {
            const res = await api.get<any>(API_ENDPOINTS.getOrderDetails(orderId));
            if (res?.data?.status === 'success') {
                const txns: any[] = res.data.transactions || [];
                const orderData = res.data.order || {};
                setOrder(orderData);
                const total = orderData.totalCost ?? orderTotal;
                // SURPLUS records the customer's over-payment (credited elsewhere, e.g. wallet) —
                // it isn't money applied toward this order, so it must not reduce what's still owed.
                const paidSum = txns.reduce((s: number, t: any) => {
                    const pm = t.paymentMethod || t.paymentType;
                    return pm === 'SURPLUS' ? s : s + (Number(t.amount) || 0);
                }, 0);
                const rem = Math.max(total - paidSum, 0);
                setOrderTotal(total);
                if (orderData.numUnits) setNumUnits(orderData.numUnits);
                setTransactions(txns);
                setPaid(paidSum);
                setRemaining(rem);
                return rem;
            }
        } catch {
            // keep existing values
        } finally {
            setLoadingOrder(false);
        }
        return null;
    }, [orderId, orderTotal]);

    useEffect(() => { fetchOrder(); }, [fetchOrder]);

    const loadUnits = useCallback(async () => {
        if (!orderId) return;
        setLoadingUnits(true);
        try {
            const result = await unitNomineeService.getOrderUnits(orderId);
            setUnits(result.data?.units || []);
        } finally {
            setLoadingUnits(false);
        }
    }, [orderId]);

    // Loaded up front (not lazily on tab click) so the Order Details card can show each
    // unit's nominee status right away, not just once the Nominee tab is opened.
    useEffect(() => { loadUnits(); }, [loadUnits]);

    // Arriving here for an order that's already fully paid (e.g. the list this was opened
    // from hadn't refreshed yet) — decided once, after BOTH the order and its units have
    // loaded (waiting on units too, not just the order, matters: it's what tells us whether
    // there's actually a nominee still missing). If there is, ask (Skip / Add Nominee)
    // instead of just leaving; otherwise there's truly nothing to do, so go back the way we
    // came. A later fetch that brings remaining to 0 (right after submitting a payment here)
    // is handled separately in handleSubmit, with its own snackbar before navigating back.
    // Skipped entirely when the caller routed here specifically for the nominee section — a
    // fully paid order still missing a nominee is the normal case for "Add/Update Nominee",
    // not a fluke to interrupt with a confirmation popup.
    const initialFullyPaidCheckDone = useRef(false);
    useEffect(() => {
        if (loadingOrder || loadingUnits || initialFullyPaidCheckDone.current) return;
        initialFullyPaidCheckDone.current = true;
        if (remaining <= 0 && activeSection !== 'nominee') {
            const fromCreateOrder = (location.state as any)?.fromCreateOrder;
            navigate(fromCreateOrder ? -2 : -1);
        }
    }, [loadingOrder, loadingUnits, remaining, activeSection, location.state, navigate]);

    const handleSelectNominee = () => setActiveSection('nominee');

    // Nominees are added only from the investor's KYC link or the KYC queue's "Fill in for
    // investor" — so a payment that completes the order just goes back.
    const finishOrPromptNominee = () => {
        const fromCreateOrder = (location.state as any)?.fromCreateOrder;
        setTimeout(() => navigate(fromCreateOrder ? -2 : -1), 1200);
    };

    const loadCoupon = useCallback(async () => {
        if (!orderId) return;
        setCouponBlock(await couponService.getOrderCoupon(orderId, adminMobile || ''));
    }, [orderId, adminMobile]);

    useEffect(() => { loadCoupon(); }, [loadCoupon]);

    const handleRemoveCoupon = async (code: string) => {
        if (!code || !orderId) return;
        setApplying(true);
        try {
            const result = await couponService.removeCoupon({ code, order_id: orderId }, adminMobile || '');
            if (result.error) {
                setSnackbar({ message: result.error, type: 'error' });
                return;
            }
            setSnackbar({ message: `Coupon ${code} removed.`, type: 'success' });
            await loadCoupon();
            fetchOrder();
        } finally {
            setApplying(false);
        }
    };

    const handleFileUpload = async (
        file: File,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        pathSuffix: string,
        errorKey?: string,
    ) => {
        if (!mobile) return;
        if (errorKey) clearErr(errorKey);
        setUpload(prev => ({ ...prev, file, url: '', uploading: true, error: '', progress: 0 }));
        try {
            let finalFile = file;
            if (file.type.startsWith('image/')) {
                try {
                    finalFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                } catch { /* use original */ }
            }
            const path = `animalkart/${storageEnv}/${userId || mobile}/payments/${Date.now()}_${pathSuffix}_${file.name}`;
            const url = await uploadToFirebase(finalFile, path, pct => setUpload(prev => ({ ...prev, progress: pct })));
            setUpload(prev => ({ ...prev, file: finalFile, url, uploading: false }));
        } catch {
            setUpload(prev => ({ ...prev, uploading: false, error: 'Upload failed. Please try again.' }));
        }
    };

    // Seeds an upload tile with a pasted image URL directly — no file, no Firebase upload.
    // OCR against it runs the same way it does for a pre-existing (edit-mode) image.
    const handleUrlSubmit = async (
        url: string,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        errorKey?: string,
    ) => {
        if (errorKey) clearErr(errorKey);

        // If it's already a Firebase URL, just use it directly to save time
        if (url.includes('firebasestorage.googleapis.com')) {
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
            return;
        }

        try {
            setUpload(prev => ({ ...prev, file: null, url: '', uploading: true, error: '', progress: 0 }));

            // Try to fetch the image and convert it to a File for Firebase
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();

            const fileName = "pasted_image" + (blob.type.includes('png') ? '.png' : '.jpg');
            const file = new File([blob], fileName, { type: blob.type });

            const pathSuffix = errorKey === 'chequeBackUpload' ? 'cheque_back' : 'proof';
            handleFileUpload(file, setUpload, pathSuffix, errorKey);
        } catch (err: any) {
            console.warn("Could not download pasted image for Firebase upload (likely CORS):", err);
            // Fallback: just use the URL directly if fetch fails
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
        }
    };

    const validate = (): Record<string, string> => {
        const errs: Record<string, string> = {};
        if (!paymentMethod) { errs.paymentMethod = 'Select a payment method'; return errs; }
        if (paymentMethod === 'COINS_REDEEM') {
            const coins = Number(coinsToRedeem) || 0;
            if (!coins || coins <= 0) errs.coinsToRedeem = 'Enter coins to redeem';
            else if (availableCoins !== null && coins > availableCoins) errs.coinsToRedeem = `Only ${availableCoins.toLocaleString('en-IN')} coins available`;
            return errs;
        }
        if (paymentMethod === 'WALLET') {
            const walletAmt = Number(walletAmountToUse) || 0;
            if (!walletAmt || walletAmt <= 0) errs.walletAmountToUse = 'Enter wallet amount to use';
            else if (availableWallet !== null && walletAmt > availableWallet) errs.walletAmountToUse = `Only ₹${availableWallet.toLocaleString('en-IN')} available in wallet`;
            return errs;
        }
        const isCoupon = paymentMethod === 'COUPON' || (paymentMethod === 'MULTIPLE' && multipleSubMode === 'COUPON');
        if (!txDate && !isCoupon) {
            errs.txDate = 'Transaction date is required';
        } else if (txDate && !isCoupon && !isValidDdmmyyyy(txDate)) {
            errs.txDate = 'Enter date in DD-MM-YYYY format';
        }

        if (!isCoupon) {
            const amountVal = Number(txAmount) || 0;
            if (!amountVal || amountVal <= 0) errs.txAmount = 'Enter payment amount';
            else if (amountVal > remainingAfterCoins) errs.txAmount = `Amount exceeds remaining balance (₹${remainingAfterCoins.toLocaleString('en-IN')})`;
        } else {
            if (!couponCode.trim()) errs.couponCode = 'Coupon code is required';
        }

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode === 'CASH_DEPOSIT') {
                    if (utrNumber.trim()) { const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1; }
                    if (paidBy !== 'Self' && accountHolderName.trim()) {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    if (bankName.trim()) { const e3 = validateBankName(bankName); if (e3) errs.bankName = e3; }
                } else {
                    const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1;
                    if (paidBy !== 'Self') {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    const e3 = validateBankName(bankName); if (e3) errs.bankName = e3;
                }
            }
            if (multipleSubMode === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (multipleSubMode === 'CHEQUE') {
                const e = validateChequeNumber(utrNumber); if (e) errs.utrNumber = e;
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
            }
            if (multipleSubMode === 'UPI') {
                const e2 = validateUTR(utrNumber); if (e2) errs.utrNumber = e2;
                if (paidBy !== 'Self' && accountHolderName.trim()) {
                    const e3 = validateAccountHolderName(accountHolderName); if (e3) errs.accountHolderName = e3;
                }
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        } else {
            if (paymentMethod === 'BANK_TRANSFER') {
                if (!screenshotUpload.url) errs.screenshotUpload = 'Payment screenshot is required';
                if (transferMode === 'CASH_DEPOSIT') {
                    if (utrNumber.trim()) { const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1; }
                    if (paidBy !== 'Self' && accountHolderName.trim()) {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    if (bankName.trim()) { const e3 = validateBankName(bankName); if (e3) errs.bankName = e3; }
                } else {
                    const e1 = validateUTR(utrNumber); if (e1) errs.utrNumber = e1;
                    if (paidBy !== 'Self') {
                        const e2 = validateAccountHolderName(accountHolderName); if (e2) errs.accountHolderName = e2;
                    }
                    const e3 = validateBankName(bankName); if (e3) errs.bankName = e3;
                }
            }
            if (paymentMethod === 'CASH') {
                if (!cashierName.trim()) errs.cashierName = 'Cashier name is required';
                if (cashierPhone.length !== 10) errs.cashierPhone = 'Enter a valid 10-digit mobile number';
                if (!voucherUpload.url) errs.voucherUpload = 'Voucher image is required';
            }
            if (paymentMethod === 'CHEQUE') {
                const e = validateChequeNumber(utrNumber); if (e) errs.utrNumber = e;
                if (!chequeFrontUpload.url) errs.chequeFrontUpload = 'Cheque front image is required';
            }
            if (paymentMethod === 'UPI') {
                const e2 = validateUTR(utrNumber); if (e2) errs.utrNumber = e2;
                if (paidBy !== 'Self' && accountHolderName.trim()) {
                    const e3 = validateAccountHolderName(accountHolderName); if (e3) errs.accountHolderName = e3;
                }
                if (!upiScreenshotUpload.url) errs.upiScreenshotUpload = 'Payment screenshot is required';
            }
        }
        return errs;
    };

    const buildTransaction = () => {
        if (!paymentMethod || paymentMethod === 'COUPON' || paymentMethod === 'COINS_REDEEM' || paymentMethod === 'WALLET') return undefined;
        const dateStr = txDate || undefined;

        if (paymentMethod === 'MULTIPLE') {
            if (multipleSubMode === 'COUPON') return undefined;
            const amt = Number(txAmount) || 0;
            const base: any = {
                paymentMethod: multipleSubMode,
                amount: amt,
                transactionDate: dateStr,
                paid_by: paidBy,
                ...(comment.trim() ? { comment: comment.trim() } : {}),
            };
            if (multipleSubMode === 'BANK_TRANSFER') {
                return {
                    ...base,
                    transferMode,
                    ...(utrNumber.trim() ? { utrNumber } : {}),
                    ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                    ...(bankName.trim() ? { bankName } : {}),
                    paymentScreenshotUrl: screenshotUpload.url,
                };
            }
            if (multipleSubMode === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
            if (multipleSubMode === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
            if (multipleSubMode === 'UPI') return { ...base, utrNumber, accountHolderName: paidBy !== 'Self' ? accountHolderName : undefined, paymentScreenshotUrl: upiScreenshotUpload.url };
            return base;
        }

        const amt = (txAmount !== '' && txAmount !== undefined && txAmount !== null) ? Number(txAmount) : remaining;
        const base: any = {
            paymentMethod,
            amount: amt,
            transactionDate: dateStr,
            paid_by: paidBy,
            ...(comment.trim() ? { comment: comment.trim() } : {}),
        };
        if (paymentMethod === 'BANK_TRANSFER') {
            return {
                ...base,
                transferMode,
                ...(utrNumber.trim() ? { utrNumber } : {}),
                ...(paidBy !== 'Self' && accountHolderName.trim() ? { accountHolderName } : {}),
                ...(bankName.trim() ? { bankName } : {}),
                paymentScreenshotUrl: screenshotUpload.url,
            };
        }
        if (paymentMethod === 'CASH') return { ...base, cashierName, cashierPhone, voucherImageUrl: voucherUpload.url };
        if (paymentMethod === 'CHEQUE') return { ...base, utrNumber, chequeFrontImage: chequeFrontUpload.url, chequeBackImage: chequeBackUpload.url };
        if (paymentMethod === 'UPI') return { ...base, utrNumber, accountHolderName: paidBy !== 'Self' ? accountHolderName : undefined, paymentScreenshotUrl: upiScreenshotUpload.url };
        return base;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0 || !orderId || !mobile) return;
        setSubmitting(true);
        try {
            if (paymentMethod === 'COUPON') {
                const res = await couponService.applyCoupon({ code: couponCode.trim(), order_id: orderId }, adminMobile);
                if (res.error) throw new Error(res.error);

                // Only leave the page once the coupon actually brought the order to fully
                // paid — same check the regular payment path below uses. Otherwise there's
                // still a balance due, so stay here and let the admin add more payment.
                const updatedRemaining = await fetchOrder();
                if (mobile) fetchAvailableCoins(mobile);

                if (updatedRemaining !== null && updatedRemaining <= 0) {
                    setSnackbar({ message: 'Coupon applied — payment completed!', type: 'success' });
                    finishOrPromptNominee();
                } else {
                    setSnackbar({ message: 'Coupon applied! You can add more payments.', type: 'success' });
                    setCouponCode('');
                    setErrors({});
                }
                return;
            }
            // paymentMode/coinsRedeemed must stay conditional on isCoins — sending paymentMode on a
            // regular bank/cash/UPI/cheque payment made the backend treat the order as settled
            // regardless of actual remaining balance (premature "Payment completed" navigation).
            const isCoins = paymentMethod === 'COINS_REDEEM';
            const isWallet = paymentMethod === 'WALLET';
            const mainTx = isCoins
                ? { amount: 0 }
                : isWallet
                    ? {
                        paymentMethod: 'MONEY_WALLET',
                        amount: Number(walletAmountToUse) || 0,
                        transactionDate: new Date().toISOString(),
                    }
                    : buildTransaction();
            const surplusNum = Number(surplusAmount) || 0;

            // confirm-payment (unlike buy-with-payment) requires the singular "transaction"
            // field — it doesn't take a "transactions" array. A surplus is sent as its own
            // follow-up call to the same endpoint, right after the main payment succeeds.
            const basePayload: any = {
                orderId,
                userId: mobile,
                paid_by: paidBy,
                paymentUpdatedByMobile: adminMobile,
                paymentUpdatedByName: adminName,
                paymentUpdatedByRole: adminRole,
                paymentUpdatedDate: new Date().toISOString().split('T')[0],
                source: 'animalkart_dashboard',
            };
            const payload: any = {
                ...basePayload,
                transaction: mainTx,
                paymentType: isCoins
                    ? undefined
                    : isWallet
                        ? 'MONEY_WALLET'
                        : (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT')
                            ? 'CASH_DEPOSIT'
                            : paymentMethod === 'MULTIPLE'
                                ? 'MULTIPLE'
                                : paymentMethod,
            };
            if (isCoins) {
                payload.paymentMode = coinsFullyCover ? 'COINS_REDEEM' : 'MANUAL_PAYMENT';
                payload.coinsRedeemed = effectiveCoinsRedeemed || 0;
            }
            if (comment.trim()) {
                if (mainTx) mainTx.comment = comment.trim();
                payload.comment = comment.trim();
            }
            const res = await api.post<any>(API_ENDPOINTS.confirmPayment(), payload);
            if (res.data?.statuscode >= 400) throw new Error(res.data?.message || 'Failed');

            // Surplus — the customer paid more than the amount due. Recorded as its own
            // SURPLUS-method transaction on the same order, so the backend can credit the
            // extra to their wallet. If this second call fails, the main payment above has
            // already gone through, so surface a distinct warning rather than a hard failure.
            let surplusFailed = false;
            if (!isCoins && surplusNum > 0) {
                try {
                    const surplusPayload: any = {
                        ...basePayload,
                        transaction: {
                            paymentMethod: 'SURPLUS',
                            amount: surplusNum,
                            transactionDate: txDate || undefined,
                            comment: surplusComment.trim() || `Customer over-paid by ${surplusNum}`,
                        },
                        paymentType: 'SURPLUS',
                    };
                    const surplusRes = await api.post<any>(API_ENDPOINTS.confirmPayment(), surplusPayload);
                    if (surplusRes.data?.statuscode >= 400) surplusFailed = true;
                } catch {
                    surplusFailed = true;
                }
            }

            const updatedRemaining = await fetchOrder();
            if (mobile) fetchAvailableCoins(mobile);

            if (surplusFailed) {
                setSnackbar({ message: 'Payment added, but the surplus amount failed to save — please add it again.', type: 'error' });
            } else if (updatedRemaining !== null && updatedRemaining <= 0) {
                setSnackbar({ message: 'Payment completed!', type: 'success' });
                finishOrPromptNominee();
            } else {
                setSnackbar({ message: 'Payment added! You can add more payments.', type: 'success' });
                setUtrNumber(''); setAccountHolderName(''); setBankName('');
                setCashierName(''); setCashierPhone(''); setCouponCode('');
                setCoinsToRedeem(''); setTxAmount('');
                setTxDate(''); setSurplusAmount(''); setSurplusComment(''); setComment('');
                setScreenshotUpload(emptyUpload()); setVoucherUpload(emptyUpload());
                setChequeFrontUpload(emptyUpload()); setChequeBackUpload(emptyUpload());
                setUpiScreenshotUpload(emptyUpload());
                setErrors({});
            }
        } catch (err: any) {
            setSnackbar({ message: err?.response?.data?.message || err?.message || 'Failed to update payment.', type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const pct = orderTotal ? Math.round((paid / orderTotal) * 100) : 0;
    // No auto-switch into the Nominee section for a fully-paid order — it only opens when
    // the admin explicitly picks it (the button below, or the fully-paid popup's "Add Nominee").
    const effectiveSection: 'payment' | 'nominee' | null = activeSection ?? (remaining > 0 ? 'payment' : null);


    return (
        <div className="w-full pb-12 font-sans">

            {/* Page header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Update Order</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 font-medium transition-colors">
                        {fromPage}
                    </button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">Update Order</span>
                </nav>
            </div>

            {loadingOrder ? (
                <div className="space-y-4">
                    {[1, 2].map(i => <div key={i} className="h-24 bg-white rounded-2xl border border-slate-200 animate-pulse" />)}
                </div>
            ) : (
                <>
                    {/* Order Details — separate, sticky card with a thick border so order
                        context stays visible while scrolling through payments below. */}
                    <div className="sticky top-4 z-20 bg-white rounded-2xl border-2 border-black px-6 py-5 space-y-4 shadow-md mb-5">
                        <div className="flex items-center justify-between">
                            <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Order Details</h4>
                            <button onClick={fetchOrder} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" title="Refresh">
                                <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex flex-wrap items-start gap-4">
                            <div className="space-y-1 w-56">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Customer Details</label>
                                <div className="min-h-[38px] px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-center gap-0.5 overflow-hidden">
                                    <span className="text-sm font-bold text-slate-800 truncate">{userName || '—'}</span>
                                    <MaskedMobile mobile={mobile} countryCode={userCountryCode} className="text-xs text-slate-500" />
                                </div>
                            </div>
                            <div className="space-y-1 w-44">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Order ID</label>
                                <div className="h-[38px] px-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                                    <span className="text-sm font-bold text-slate-800 font-mono">{orderId || '—'}</span>
                                </div>
                            </div>
                            <div className="space-y-1 w-32">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Units</label>
                                <div className="h-[38px] px-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                                    <span className="text-sm font-bold text-slate-800">{numUnits || '—'}</span>
                                </div>
                            </div>
                            <div className="space-y-1 w-36">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Total Cost</label>
                                <div className="h-[38px] px-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                                    <span className="text-sm font-bold text-slate-800">{fmt(orderTotal)}</span>
                                </div>
                            </div>
                            <div className="space-y-1 w-36">
                                <label className="block text-[10px] font-semibold text-green-500 uppercase tracking-wide whitespace-nowrap">Paid</label>
                                <div className="h-[38px] px-3 bg-green-50/50 border border-green-100 rounded-lg flex items-center">
                                    <span className="text-sm font-bold text-green-600">{fmt(paid)}</span>
                                </div>
                            </div>
                            <div className="space-y-1 w-36">
                                <label className="block text-[10px] font-semibold text-amber-500 uppercase tracking-wide whitespace-nowrap">Remaining</label>
                                <div className="h-[38px] px-3 bg-amber-50/50 border border-amber-100 rounded-lg flex items-center">
                                    <span className="text-sm font-bold text-amber-600">{fmt(remaining)}</span>
                                </div>
                            </div>
                            <div className="space-y-1 flex-1 min-w-[200px]">
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Progress ({pct}%)</label>
                                <div className="h-[38px] px-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {units.length > 0 && (
                            <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-x-6 gap-y-1.5">
                                {units.map((u, i) => (
                                    <p key={u.unit_id} className="text-xs">
                                        <span className="font-semibold text-slate-500">Unit {i + 1}:</span>{' '}
                                        {u.nominee
                                            ? <span className="text-slate-800">{u.nominee.nominee_full_name} ({u.nominee.relation})</span>
                                            : <span className="text-slate-400">No nominee added</span>}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Section switcher — Update Payment (still-partial orders only) and the
                        unit nominees (view only). Only one is shown at a time. */}
                    <div className="flex gap-3 mb-5">
                        {remaining > 0 && (
                            <button type="button" onClick={() => setActiveSection('payment')}
                                className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm border transition-colors ${effectiveSection === 'payment' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                                Update Payment
                            </button>
                        )}
                        {isEmployee && !(order?.PHASE_1_READY_TO_DELIVER || order?.READY_TO_DELIVER) && (
                            <button type="button" onClick={handleSelectNominee}
                                className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm border transition-colors flex items-center justify-center gap-2 ${effectiveSection === 'nominee' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                                {loadingUnits && <Loader2 size={13} className="animate-spin" />}
                                Nominees
                            </button>
                        )}
                    </div>

                    {effectiveSection === 'nominee' && (
                        <div className="space-y-4 mb-5">
                            {loadingUnits && <p className="text-xs text-slate-400 px-2">Loading units…</p>}
                            {!loadingUnits && units.length === 0 && <p className="text-xs text-slate-400 px-2">No units found for this order.</p>}
                            {/* One card per unit, stacked — not a tab switcher — so every unit's
                                nominee is visible at once. View only: nominees come from the investor's
                                KYC link or the KYC queue's "Fill in for investor". */}
                            {!loadingUnits && units.map((u, i) => {
                                return (
                                <div key={u.unit_id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-bold text-slate-800">Unit {i + 1}</p>
                                        <span className="text-[10px] text-slate-400 font-mono truncate">{u.unit_id}</span>
                                        {!u.nominee && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                                    </div>
                                    {u.nominee ? (
                                            <div className="px-6 py-5 space-y-5">
                                                <div className="flex flex-wrap justify-between gap-x-6 gap-y-4 sm:gap-x-8">
                                                    <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Nominee Name</span><div className="text-sm font-semibold text-slate-800">{u.nominee.nominee_full_name}</div></div>
                                                    <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Relation</span><div className="text-sm font-semibold text-slate-800">{u.nominee.relation}</div></div>
                                                    <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Mobile</span><div className="text-sm font-semibold text-slate-800">+{String(u.nominee.country_code || '91').replace('+', '')} {u.nominee.mobile_no}</div></div>
                                                    <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Aadhaar Number</span><div className="text-sm font-semibold text-slate-800">{u.nominee.aadhar_number}</div></div>
                                                    {u.nominee.gender && <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gender</span><div className="text-sm font-semibold text-slate-800">{u.nominee.gender}</div></div>}
                                                    {u.nominee.pancard_number && <div className="flex flex-col gap-1"><span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">PAN Number</span><div className="text-sm font-semibold text-slate-800">{u.nominee.pancard_number}</div></div>}
                                                </div>

                                                {(u.nominee.aadhar_photo_url || u.nominee.pancard_photo_url || u.nominee.guardian_certificate_link) && (
                                                    <>
                                                        <div className="h-px bg-slate-100 w-full" />
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            {u.nominee.aadhar_photo_url && (
                                                                <div className="border border-slate-200 rounded-xl p-2 bg-slate-50/50">
                                                                    <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2">
                                                                        Aadhaar Card Photo
                                                                    </div>
                                                                    <img
                                                                        src={u.nominee.aadhar_photo_url}
                                                                        alt="Aadhaar Card"
                                                                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-slate-200 bg-white"
                                                                        onClick={() => setPreviewUrl({ url: u.nominee!.aadhar_photo_url!, plain: true })}
                                                                    />
                                                                </div>
                                                            )}
                                                            {u.nominee.pancard_photo_url && (
                                                                <div className="border border-slate-200 rounded-xl p-2 bg-slate-50/50">
                                                                    <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2">
                                                                        PAN Card Photo
                                                                    </div>
                                                                    <img
                                                                        src={u.nominee.pancard_photo_url}
                                                                        alt="PAN Card"
                                                                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-slate-200 bg-white"
                                                                        onClick={() => setPreviewUrl({ url: u.nominee!.pancard_photo_url!, plain: true })}
                                                                    />
                                                                </div>
                                                            )}
                                                            {u.nominee.guardian_certificate_link && (
                                                                <div className="border border-slate-200 rounded-xl p-2 bg-slate-50/50">
                                                                    <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2">
                                                                        Guardian Certificate
                                                                    </div>
                                                                    <img
                                                                        src={u.nominee.guardian_certificate_link}
                                                                        alt="Guardian Certificate"
                                                                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border border-slate-200 bg-white"
                                                                        onClick={() => setPreviewUrl({ url: u.nominee!.guardian_certificate_link!, plain: true })}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}

                                                {(u.nominee.added_by_name || (u.nominee.history && u.nominee.history.length > 0)) && (
                                                    <>
                                                        <div className="h-px bg-slate-100 w-full" />
                                                        {/* Same "Created By" / "Edited By" card format as the transaction
                                                            footer on Order Details, instead of plain Added/Updated By text. */}
                                                        <div className="space-y-2 text-xs">
                                                            {u.nominee.added_by_name && (
                                                                <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Created By</span>
                                                                        {u.nominee.createdAt && (
                                                                            <span className="text-slate-500 font-semibold shrink-0">{formatRawDateTime(u.nominee.createdAt)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap text-slate-800 font-semibold">
                                                                        {u.nominee.added_by_name}
                                                                        {u.nominee.added_by_number && (
                                                                            <span className="text-slate-400 font-normal">
                                                                                (<MaskedMobile mobile={u.nominee.added_by_number} className="text-slate-400" />)
                                                                            </span>
                                                                        )}
                                                                        {u.nominee.added_by_role && (
                                                                            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 uppercase font-bold tracking-tight shrink-0">
                                                                                {u.nominee.added_by_role}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* The CREATED history entry just restates the Created By card
                                                                above (same name/role/timestamp), so only UPDATED entries
                                                                get their own card here. */}
                                                            {(u.nominee.history || []).filter(h => h.action !== 'CREATED').reverse().map((h, hi) => (
                                                                <div key={hi} className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                                        <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                                            <Pencil size={12} className="shrink-0" />
                                                                            Updated By
                                                                        </span>
                                                                        <span className="text-slate-500 font-semibold shrink-0">{formatRawDateTime(h.at)}</span>
                                                                    </div>
                                                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap text-slate-800 font-semibold">
                                                                        {h.by_name || 'System'}
                                                                        {h.by_number && (
                                                                            <span className="text-slate-400 font-normal">
                                                                                (<MaskedMobile mobile={h.by_number} className="text-slate-400" />)
                                                                            </span>
                                                                        )}
                                                                        {h.by_role && (
                                                                            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 uppercase font-bold tracking-tight shrink-0">
                                                                                {h.by_role}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {(() => {
                                                                        // The certificate is a long URL, not something worth
                                                                        // showing as an inline old → new text diff.
                                                                        const visibleChanges = Object.entries(h.changes || {}).filter(([field]) => field !== 'guardian_certificate_link');
                                                                        return visibleChanges.length > 0 && (
                                                                        <div className="mt-1.5 pt-1.5 border-t border-slate-200">
                                                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Changed Fields</div>
                                                                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                                                {visibleChanges.map(([field, diff]) => (
                                                                                    <span key={field} className="text-slate-700">
                                                                                        <span className="text-slate-400 font-medium capitalize">{field.replace(/_/g, ' ')}:</span>{' '}
                                                                                        <span className="line-through text-slate-400">{String(diff.old ?? '—')}</span>
                                                                                        {' → '}
                                                                                        <span className="text-slate-700 font-medium">{String(diff.new ?? '—')}</span>
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 px-6 py-5">No nominee yet — the investor adds it from their KYC link.</p>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Previous Payments card — kept separate from the Add Payment card below */}
                    {effectiveSection === 'payment' && transactions.length > 0 && (
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-6 space-y-4 mb-5">
                            <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Previous Payments</h4>
                            {transactions.map((tx: any, i: number) => {
                                const txd = { ...(tx.transaction || {}), ...tx };
                                const method = txd.paymentMethod || txd.paymentType || '';
                                const isBank = method === 'BANK_TRANSFER' || method === 'CASH_DEPOSIT';
                                const isCheque = method === 'CHEQUE';
                                const isCash = method === 'CASH';
                                const isUpi = method === 'UPI' || method === 'ONLINE';
                                const isCoins = method === 'COINS_REDEEM';
                                const isCoupon = method === 'COUPON';
                                const isSurplus = method === 'SURPLUS';
                                const isWallet = method === 'MONEY_WALLET';
                                const isNoImage = isCoins || isSurplus || isWallet;
                                const frontImg = txd.chequeFrontImage || txd.frontImageUrl || txd.front_image_url || txd.frontImage;
                                const backImg = txd.chequeBackImage || txd.backImageUrl || txd.back_image_url || txd.backImage;
                                const proofImg = txd.voucherImageUrl || txd.voucher_image_url || txd.paymentScreenshotUrl || txd.screenshot || txd.paymentProof || txd.payment_proof_Url || txd.proofImage;
                                const label = methodLabel(method || '—');
                                const couponTxId: string = txd.id || '';
                                const couponCodeFromId = couponTxId.includes('-TX-COUPON-') ? couponTxId.split('-TX-COUPON-').pop() : undefined;
                                const resolvedCouponCode = isCoupon
                                    ? (txd.couponCode || txd.code || couponCodeFromId || couponBlock?.coupons?.find((c: any) => Number(c.amount) === Number(txd.amount))?.code)
                                    : undefined;
                                const matchedCoupon = isCoupon ? couponBlock?.coupons?.find((c: any) => c.code === resolvedCouponCode) : undefined;

                                return (
                                    <div key={txd.id || i} className="flex flex-col gap-4 p-5 bg-white border border-slate-200 hover:border-slate-300 rounded-2xl shadow-sm transition-all">
                                        {/* Card Header: Title & Method Badge */}
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 border border-slate-100 shrink-0">
                                                    <CreditCard size={16} />
                                                </div>
                                                <span className="text-sm font-bold text-slate-800">Transaction Details</span>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 uppercase">{label}</span>
                                                {/* Remove button for COUPON transactions */}
                                                {isCoupon && canRemoveCouponPerm && ['PENDING_PAYMENT', 'PENDING_SUBMISSION', 'PENDING_ADMIN_VERIFICATION'].includes(order?.paymentStatus) && (
                                                    <button
                                                        onClick={() => resolvedCouponCode && handleRemoveCoupon(resolvedCouponCode)}
                                                        disabled={applying || !resolvedCouponCode}
                                                        className="px-2.5 py-1 text-[11px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-100 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                                                    >
                                                        {applying ? 'Removing…' : 'Remove'}
                                                    </button>
                                                )}
                                                {(
                                                    // SuperAdmin correction: one-time, only in super-admin verification states. Admin no longer gets edit access.
                                                    (isSuperAdmin && !(isSuperAdmin && order?.paymentStatus === 'PENDING_ADMIN_VERIFICATION') && ['PENDING_ADMIN_VERIFICATION', 'PENDING_SUPER_ADMIN_VERIFICATION', 'PENDING_SUPER_ADMIN_REJECTION'].includes(order?.paymentStatus) && !txd.isEdited)
                                                    // Staff draft (Employee/Supervisor/Cashier): editable while held / payment-due.
                                                    // Gated by the users.edit_order sub-action (canEditOrderUsers below).
                                                    || (!isAdmin && !isSuperAdmin && ['PENDING_SUBMISSION', 'PENDING_PAYMENT'].includes(order?.paymentStatus))
                                                ) && method !== 'COUPON' && method !== 'MONEY_WALLET' && method !== 'SURPLUS' && canEditOrderUsers && (
                                                        <button
                                                            onClick={() => setEditingTx(tx)}
                                                            title="Edit Transaction"
                                                            className="p-1.5 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-all border-none cursor-pointer flex items-center justify-center"
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                    )}
                                            </div>
                                        </div>

                                        {/* 3-Column Table Grid Container — coin redemptions and surplus payments have no proof image,
                                                    so the text fields take the full width instead of leaving an empty column */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 border border-slate-100 rounded-xl overflow-hidden text-xs bg-white">
                                            {/* Columns 1 & 2: Text Fields */}
                                            <div className={`${isNoImage ? 'md:col-span-3' : 'md:col-span-2'} grid grid-cols-1 sm:grid-cols-2 divide-y divide-x divide-slate-100`}>
                                                <div className="p-3 bg-white flex flex-col gap-0.5">
                                                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Payment Mode</span>
                                                    <span className="text-sm font-extrabold text-blue-700">{label}</span>
                                                </div>
                                                <div className="p-3 bg-white flex flex-col gap-0.5">
                                                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{isCoins ? 'Redemption Value' : isCoupon ? 'Discount Amount' : 'Paid Amount'}</span>
                                                    <span className="text-sm font-extrabold text-slate-900">₹{Number(txd.amount || 0).toLocaleString('en-IN')}</span>
                                                </div>

                                                {txd.transactionDate && (
                                                    <div className="p-3 bg-white flex flex-col gap-0.5">
                                                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Transaction Date</span>
                                                        <span className="text-xs font-semibold text-slate-800">{txd.transactionDate}</span>
                                                    </div>
                                                )}
                                                {txd.paid_by && (
                                                    <div className="p-3 bg-white flex flex-col gap-0.5">
                                                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Paid By</span>
                                                        <span className="text-xs font-semibold text-slate-800">{txd.paid_by}</span>
                                                    </div>
                                                )}

                                                {isBank && (<>
                                                    {txd.transferMode && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Transfer Mode</span>
                                                            <span className="text-xs font-semibold text-slate-800">{transferModeLabel(txd.transferMode)}</span>
                                                        </div>
                                                    )}
                                                    {txd.utrNumber && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">UTR Number</span>
                                                            <span className="text-xs font-bold text-blue-700">{txd.utrNumber}</span>
                                                        </div>
                                                    )}
                                                    {txd.bankName && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Bank Name</span>
                                                            <span className="text-xs font-semibold text-slate-800">{txd.bankName}</span>
                                                        </div>
                                                    )}
                                                    {txd.accountHolderName && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Account Holder</span>
                                                            <span className="text-xs font-semibold text-slate-800">{txd.accountHolderName}</span>
                                                        </div>
                                                    )}
                                                </>)}

                                                {isCheque && (<>
                                                    {txd.utrNumber && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Cheque Number</span>
                                                            <span className="text-xs font-bold text-blue-700">{txd.utrNumber}</span>
                                                        </div>
                                                    )}
                                                    {txd.bankName && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Bank Name</span>
                                                            <span className="text-xs font-semibold text-slate-800">{txd.bankName}</span>
                                                        </div>
                                                    )}
                                                </>)}

                                                {isCash && (<>
                                                    {txd.cashierName && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Cashier Name</span>
                                                            <span className="text-xs font-semibold text-slate-800">{txd.cashierName}</span>
                                                        </div>
                                                    )}
                                                    {txd.cashierPhone && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Cashier Phone</span>
                                                            <span className="text-xs font-semibold text-slate-800"><MaskedMobile mobile={txd.cashierPhone} /></span>
                                                        </div>
                                                    )}
                                                </>)}

                                                {isCoupon && (<>
                                                    {resolvedCouponCode && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Coupon Code</span>
                                                            <span className="text-xs font-bold text-blue-700">{resolvedCouponCode}</span>
                                                        </div>
                                                    )}
                                                    {matchedCoupon?.unit_index != null && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Unit</span>
                                                            <span className="text-xs font-semibold text-slate-800">{matchedCoupon.unit_index}</span>
                                                        </div>
                                                    )}
                                                    {matchedCoupon?.applied_by_name && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Applied By</span>
                                                            <span className="text-xs font-semibold text-slate-800">{matchedCoupon.applied_by_name}{matchedCoupon.applied_by_role ? ` (${matchedCoupon.applied_by_role})` : ''}</span>
                                                        </div>
                                                    )}
                                                </>)}

                                                {isUpi && (<>
                                                    {txd.upiId && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">UPI ID</span>
                                                            <span className="text-xs font-bold text-blue-700">{txd.upiId}</span>
                                                        </div>
                                                    )}
                                                    {txd.utrNumber && (
                                                        <div className="p-3 bg-white flex flex-col gap-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">UTR Number</span>
                                                            <span className="text-xs font-bold text-blue-700">{txd.utrNumber}</span>
                                                        </div>
                                                    )}
                                                </>)}

                                                {/* Transaction ID */}
                                                <div className="p-3 bg-white flex flex-col gap-0.5">
                                                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Transaction ID</span>
                                                    <span className="text-xs text-slate-700 font-mono select-all break-all">{txd.id || '—'}</span>
                                                </div>
                                            </div>

                                            {/* Column 3: Payment Proof — omitted entirely for coin redemptions and surplus payments, since
                                                        those never have a proof image (nothing was uploaded to show). */}
                                            {!isNoImage && (
                                                <div className="bg-slate-50/20 p-3 flex flex-col gap-1.5 border-t md:border-t-0 md:border-l border-slate-100 min-h-[160px] md:min-h-0 h-full w-full">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block px-1">Payment Proof</span>
                                                    {isCheque ? (
                                                        (frontImg || backImg) ? (
                                                            <div className={`flex-1 grid gap-2 ${frontImg && backImg ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                                                {frontImg && (
                                                                    <button
                                                                        onClick={() => setPreviewUrl({ url: frontImg, plain: true })}
                                                                        className="relative w-full h-full min-h-[120px] rounded-lg border border-slate-200 overflow-hidden hover:opacity-90 active:scale-[0.98] transition-all bg-slate-50 group"
                                                                        title="Click to zoom cheque front"
                                                                    >
                                                                        <img src={frontImg} alt="Cheque Front" className="w-full h-full object-contain absolute inset-0 p-1" />
                                                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10">
                                                                            <span className="text-[10px] font-bold text-white bg-black/60 px-2 py-1 rounded-md shadow">Zoom</span>
                                                                        </div>
                                                                        <span className="absolute bottom-1 left-1 right-1 text-center text-[9px] font-bold text-white bg-black/50 rounded px-1 py-0.5">Front</span>
                                                                    </button>
                                                                )}
                                                                {backImg && (
                                                                    <button
                                                                        onClick={() => setPreviewUrl({ url: backImg, plain: true })}
                                                                        className="relative w-full h-full min-h-[120px] rounded-lg border border-slate-200 overflow-hidden hover:opacity-90 active:scale-[0.98] transition-all bg-slate-50 group"
                                                                        title="Click to zoom cheque back"
                                                                    >
                                                                        <img src={backImg} alt="Cheque Back" className="w-full h-full object-contain absolute inset-0 p-1" />
                                                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10">
                                                                            <span className="text-[10px] font-bold text-white bg-black/60 px-2 py-1 rounded-md shadow">Zoom</span>
                                                                        </div>
                                                                        <span className="absolute bottom-1 left-1 right-1 text-center text-[9px] font-bold text-white bg-black/50 rounded px-1 py-0.5">Back</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex-1 w-full h-full rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-slate-400 bg-white min-h-[120px]">
                                                                <span className="text-xs font-medium">No proof uploaded</span>
                                                            </div>
                                                        )
                                                    ) : proofImg ? (
                                                        <button
                                                            onClick={() => setPreviewUrl({ url: proofImg, plain: true })}
                                                            className="flex-1 w-full h-full rounded-lg border border-slate-200 overflow-hidden hover:opacity-90 active:scale-[0.98] transition-all bg-slate-50 relative group min-h-[120px]"
                                                            title="Click to zoom proof"
                                                        >
                                                            <img src={proofImg} alt="Proof" className="w-full h-full object-contain absolute inset-0 p-1" />
                                                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10">
                                                                <span className="text-[10px] font-bold text-white bg-black/60 px-2 py-1 rounded-md shadow">Zoom Image</span>
                                                            </div>
                                                        </button>
                                                    ) : (
                                                        <div className="flex-1 w-full h-full rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-slate-400 bg-white min-h-[120px]">
                                                            <span className="text-xs font-medium">No proof uploaded</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Footer Section */}
                                        <div className="border-t border-slate-100 pt-4 mt-1 flex flex-col gap-3 text-xs">
                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                {/* Added By details */}
                                                {(txd.paymentUpdatedByName || txd.paymentUpdatedByRole) ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Added By</span>
                                                        <span className="flex items-center flex-wrap gap-1 text-xs font-semibold text-slate-700">
                                                            {txd.paymentUpdatedByName || 'System'}
                                                            {txd.paymentUpdatedByMobile && (
                                                                <span className="text-slate-400 italic font-normal">(<MaskedMobile mobile={txd.paymentUpdatedByMobile} className="text-slate-400 italic" />)</span>
                                                            )}
                                                            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 uppercase font-bold tracking-tight">
                                                                {txd.paymentUpdatedByRole || 'Admin'}
                                                            </span>
                                                            {txd.paymentUpdatedDate && <span className="text-slate-400 font-normal">· {formatRawDateTime(txd.paymentUpdatedDate)}</span>}
                                                        </span>
                                                    </div>
                                                ) : <div />}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Add Payment card — only shown under the Update Payment option, and only
                        while a balance remains (a fully-paid order can't reach this section). */}
                    {effectiveSection === 'payment' && (remaining <= 0 ? (
                        <div className="bg-white rounded-2xl border border-green-200 shadow-sm px-6 py-6 flex items-center gap-2 mb-5">
                            <CheckCircle size={16} className="text-green-500" />
                            <p className="text-sm font-semibold text-green-700">Payment completed — nothing left to pay on this order.</p>
                        </div>
                    ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col mb-5 divide-y divide-slate-200">
                        {/* Row 1: Payment Details & Admin Details */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 gap-4">
                            <h4 className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Payment Details</h4>
                            <div className="flex items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                <User size={12} className="text-slate-400" />
                                <span className="font-semibold text-slate-700">{adminName}</span>
                                <span className="text-slate-300">·</span>
                                <span>{adminRole}</span>
                                <span className="text-slate-300">·</span>
                                <span>{adminMobile}</span>
                            </div>
                        </div>

                        {/* Row 2: Payment Methods */}
                        <div className="p-6 space-y-1.5">
                            <label className="block text-xs font-medium text-slate-600">Payment Methods</label>
                            <div className="flex flex-wrap gap-2">
                                {PAYMENT_METHOD_OPTIONS.map(({ key, label, icon }) => (
                                    <button key={key} type="button"
                                        onClick={() => { setPaymentMethod(key); resetPaymentSubFields(); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === key ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        {icon}{label}
                                    </button>
                                ))}
                                {!loadingCoins && availableCoins !== null && (
                                    <button type="button"
                                        onClick={() => { setPaymentMethod('COINS_REDEEM'); resetPaymentSubFields(); setCoinsToRedeem(String(Math.min(availableCoins ?? 0, remaining))); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === 'COINS_REDEEM' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        <span className="text-base leading-none">🪙</span>Coins
                                    </button>
                                )}
                                {!loadingWallet && availableWallet !== null && (
                                    <button type="button"
                                        onClick={() => { setPaymentMethod('WALLET'); resetPaymentSubFields(); setWalletAmountToUse(String(Math.min(availableWallet ?? 0, remaining))); }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === 'WALLET' ? 'bg-[#1B4332] text-white border-[#1B4332] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4332] hover:text-[#1B4332]'}`}>
                                        <Wallet size={15} />Wallet
                                    </button>
                                )}
                            </div>
                            {errors.paymentMethod && <p className="text-[10px] text-red-500">{errors.paymentMethod}</p>}
                        </div>

                        {/* Row 3: Image & Fields */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                            {/* Left Column: Image Preview */}
                            <div className="p-6 flex flex-col">
                                <div className="min-h-[300px] flex flex-col">
                                    {paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' && !isCouponMode ? (
                                        <ImagePreviewModal
                                            inline
                                            part="image"
                                            url={activeUpload.url || null}
                                            uploadLabel={activeUploadLabel}
                                            uploading={activeUpload.uploading}
                                            uploadProgress={activeUpload.progress}
                                            uploadErrorMsg={activeUpload.error || errors[activeUploadErrorKey]}
                                            onUpload={f => {
                                                // Replacing an existing upload — any fields auto-filled from the
                                                // old image's OCR text are now stale, so clear them rather than
                                                // leave a mismatched value silently committed.
                                                if (activeUpload.url) {
                                                    clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode });
                                                    setVerifiedFields(new Set());
                                                }
                                                handleFileUpload(f, setActiveUpload, 'proof', activeUploadErrorKey);
                                            }}
                                            onUrlSubmit={url => {
                                                if (activeUpload.url) {
                                                    clearAutoFilledFields({ setUtrNumber, setBankName, setTxAmount, setTxDate, setTransferMode });
                                                    setVerifiedFields(new Set());
                                                }
                                                handleUrlSubmit(url, setActiveUpload, activeUploadErrorKey);
                                            }}
                                            onClear={() => setActiveUpload(emptyUpload())}
                                            onImageZoom={url => setPreviewUrl({ url, plain: true })}
                                            secondaryUpload={activeMode === 'CHEQUE' ? {
                                                label: 'Cheque Back Image (Optional)',
                                                url: chequeBackUpload.url,
                                                uploading: chequeBackUpload.uploading,
                                                error: chequeBackUpload.error,
                                                onUpload: f => handleFileUpload(f, setChequeBackUpload, 'cheque_back', 'chequeBackUpload'),
                                                onUrlSubmit: url => handleUrlSubmit(url, setChequeBackUpload, 'chequeBackUpload'),
                                                onClear: () => setChequeBackUpload(emptyUpload()),
                                                onZoom: url => setPreviewUrl({ url, plain: true }),
                                            } : undefined}
                                        />
                                    ) : (
                                        <div className="flex-1 w-full h-full rounded-lg border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 bg-slate-50 min-h-[200px]">
                                            <span className="text-xs font-medium">Select a payment method</span>
                                            <span className="text-[10px]">to upload a document</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Column: Form Fields & Extracted Text */}
                            <div className="flex flex-col h-full divide-y divide-slate-200">
                                {/* Top Row: Form Fields */}
                                <div className="p-6 flex-1">
                                    <div className="space-y-5">
                                        {/* MULTIPLE: remaining balance banner + sub-mode chips */}
                                        {paymentMethod === 'MULTIPLE' && (
                                            <>
                                                <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                                    <span className="text-xs text-blue-600 font-medium">Remaining Balance</span>
                                                    <span className="text-sm font-bold text-blue-800">₹{remainingAfterCoins.toLocaleString('en-IN')}</span>
                                                </div>
                                                <div className="flex gap-2 flex-wrap">
                                                    {MULTIPLE_SUB_OPTIONS.map(({ key, label, icon }) => (
                                                        <button key={key} type="button" onClick={() => { setMultipleSubMode(key); resetPaymentSubFields(); }}
                                                            className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold border transition-all ${multipleSubMode === key ? 'bg-purple-600 text-white border-purple-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-400 hover:text-purple-600'}`}>
                                                            {icon}{label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {paymentMethod === 'COINS_REDEEM' ? (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                                {(availableCoins ?? 0) <= 0 ? (
                                                    <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-500 text-center">
                                                        🪙 No coins available
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <div className="space-y-1 w-full">
                                                            <div className="flex items-center justify-between">
                                                                <label className="block text-sm font-medium text-slate-600">
                                                                    Coins <span className="text-black font-normal">(Available: {(availableCoins ?? 0).toLocaleString('en-IN')} coins)</span> <span className="text-red-500">*</span>
                                                                </label>
                                                                <button type="button"
                                                                    onClick={() => { setCoinsToRedeem(String(Math.min(availableCoins ?? 0, remaining))); clearErr('coinsToRedeem'); }}
                                                                    className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                                            </div>
                                                            <input
                                                                type="number" min={1}
                                                                value={coinsToRedeem}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                                onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                                                onChange={e => {
                                                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                    const numVal = raw === '' ? 0 : Number(raw);
                                                                    const avail = Math.min(availableCoins ?? 0, remaining);
                                                                    if (raw !== '' && numVal > avail) return;
                                                                    setCoinsToRedeem(raw);
                                                                    clearErr('coinsToRedeem');
                                                                }}
                                                                placeholder={`Enter coins (Available: ${(availableCoins ?? 0).toLocaleString('en-IN')})`}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.coinsToRedeem ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-amber-500'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                            />
                                                            {errors.coinsToRedeem && <p className="text-[10px] text-red-500">{errors.coinsToRedeem}</p>}
                                                            {Math.floor(Number(coinsToRedeem) || 0) > 0 && (
                                                                <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(Math.floor(Number(coinsToRedeem)))} Coins Only</p>
                                                            )}
                                                        </div>
                                                        {coinsFullyCover && (
                                                            <div className="rounded-lg px-3 py-2 text-xs font-bold bg-green-50 border border-green-200 text-green-700 text-center">
                                                                ✓ Order fully covered by coins!
                                                            </div>
                                                        )}
                                                        <div className="space-y-1 w-full pt-1">
                                                            <label className="block text-xs font-medium text-slate-600">Comment <span className="text-slate-400 font-normal">(optional)</span></label>
                                                            <input
                                                                type="text"
                                                                value={comment}
                                                                onChange={e => setComment(e.target.value)}
                                                                placeholder="Add an optional comment/note"
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1B4332]"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : paymentMethod === 'WALLET' ? (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                                {(availableWallet ?? 0) <= 0 ? (
                                                    <div className="rounded-lg px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-500 text-center">
                                                        💼 No wallet balance available
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <div className="space-y-1 w-full">
                                                            <div className="flex items-center justify-between">
                                                                <label className="block text-sm font-medium text-slate-600">
                                                                    Wallet Amount <span className="text-black font-normal">(Available: ₹{(availableWallet ?? 0).toLocaleString('en-IN')})</span> <span className="text-red-500">*</span>
                                                                </label>
                                                                <button type="button"
                                                                    onClick={() => { setWalletAmountToUse(String(Math.min(availableWallet ?? 0, remaining))); clearErr('walletAmountToUse'); }}
                                                                    className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                                            </div>
                                                            <input
                                                                type="number" min={1}
                                                                value={walletAmountToUse}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                                onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                                                onChange={e => {
                                                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                                                    const numVal = raw === '' ? 0 : Number(raw);
                                                                    const avail = Math.min(availableWallet ?? 0, remaining);
                                                                    if (raw !== '' && numVal > avail) return;
                                                                    setWalletAmountToUse(raw);
                                                                    clearErr('walletAmountToUse');
                                                                }}
                                                                placeholder={`Enter wallet amount (Available: ₹${(availableWallet ?? 0).toLocaleString('en-IN')})`}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${errors.walletAmountToUse ?'border-red-300 focus:border-red-500':'border-slate-200 focus:border-[#1B4332]'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                                            />
                                                            {errors.walletAmountToUse && <p className="text-[10px] text-red-500">{errors.walletAmountToUse}</p>}
                                                            {Math.floor(Number(walletAmountToUse) || 0) > 0 && (
                                                                <p className="text-[10px] font-bold text-slate-900 mt-1.5">{numberToWords(Math.floor(Number(walletAmountToUse)))} Rupees Only</p>
                                                            )}
                                                        </div>
                                                        <div className="space-y-1 w-full pt-1">
                                                            <label className="block text-xs font-medium text-slate-600">Comment <span className="text-slate-400 font-normal">(optional)</span></label>
                                                            <input
                                                                type="text"
                                                                value={comment}
                                                                onChange={e => setComment(e.target.value)}
                                                                placeholder="Add an optional comment/note"
                                                                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1B4332]"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : isCouponMode ? (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                                                <div className="space-y-1 w-full max-w-sm">
                                                    <label className="block text-xs font-medium text-slate-600">Coupon Code <span className="text-red-500">*</span></label>
                                                    <Select
                                                        value={couponCode}
                                                        onChange={v => setCouponCode(v)}
                                                        disabled={loadingUserCoupons || userCoupons.length === 0}
                                                        placeholder={loadingUserCoupons ? 'Loading coupons…' : userCoupons.length === 0 ? 'No coupons available' : 'Select a coupon'}
                                                        options={userCoupons.map(c => ({
                                                            value: c.code,
                                                            label: `${c.code} — ₹${Number(c.amount).toLocaleString('en-IN')}`,
                                                        }))}
 className={`w-full px-3 py-2 rounded-lg border text-sm outline-none disabled:opacity-50 disabled:bg-slate-100 bg-white ${errors.couponCode ?'border-red-300':'border-slate-200 focus:border-[#1B4332]'}`}
                                                    />
                                                    {!loadingUserCoupons && userCoupons.length === 0 && (
                                                        <p className="text-[10px] text-slate-500">This customer has no coupons assigned.</p>
                                                    )}
                                                    {errors.couponCode && <p className="text-[10px] text-red-500">{errors.couponCode}</p>}
                                                </div>
                                                {couponCode && (() => {
                                                    const selectedCoupon = userCoupons.find(c => c.code === couponCode);
                                                    if (!selectedCoupon) return null;
                                                    const couponAmt = Number(selectedCoupon.amount) || 0;
                                                    const newRemaining = Math.max(remainingAfterCoins - couponAmt, 0);
                                                    return (
                                                        <div className="w-full max-w-sm bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 space-y-1.5">
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-slate-600">Remaining Balance</span>
                                                                <span className="font-semibold text-slate-800">₹{remainingAfterCoins.toLocaleString('en-IN')}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <span className="text-slate-600">Coupon Amount</span>
                                                                <span className="font-semibold text-red-600">- ₹{couponAmt.toLocaleString('en-IN')}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center text-sm pt-1.5 border-t border-blue-200">
                                                                <span className="font-medium text-blue-700">Remaining After Coupon</span>
                                                                <span className="font-bold text-blue-800">₹{newRemaining.toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : paymentMethod && paymentMethod !== 'NONE' && (
                                            // Extracted-fields + Surplus + Full Extracted Text — the
                                            // upload/image side is rendered separately, in the left column.
                                            <ImagePreviewModal
                                                inline
                                                part="fields"
                                                showSurplus
                                                surplusAmount={surplusAmount}
                                                onSurplusChange={setSurplusAmount}
                                                surplusComment={surplusComment}
                                                onSurplusCommentChange={setSurplusComment}
                                                comment={comment}
                                                onCommentChange={setComment}
                                                url={activeUpload.url || null} text={ocrText} loading={ocrLoading}
                                                activePaymentType={activeMode}
                                                remaining={remainingAfterCoins || remaining}
                                                fieldErrors={errors}
                                                onUtrDuplicate={setUtrDuplicate}
                                                onAutoFill={values => applyAutoFillValues(
                                                    values,
                                                    { setTxAmount, setTxDate, setTransferMode, setPaidBy, setUtrNumber, setAccountHolderName, setBankName, setCashierName, setCashierPhone },
                                                )}
                                                onFieldVerified={key => setVerifiedFields(prev => new Set(prev).add(key))}
                                                onFieldEdited={onFieldEdited}
                                            />
                                        )}
                                    </div>
                                </div>
                                
                                {/* Bottom Row: Extracted Text */}
                                {paymentMethod && paymentMethod !== 'NONE' && paymentMethod !== 'COINS_REDEEM' && paymentMethod !== 'WALLET' && !isCouponMode && (
                                    <div className="p-6 bg-slate-50/30">
                                        <ImagePreviewModal
                                            inline
                                            part="text"
                                            url={activeUpload.url || null} text={ocrText} loading={ocrLoading}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions — inside the Add Payment box */}
                        <div className="flex gap-3 pt-2 border-t border-slate-100">
                            <button type="button" onClick={() => navigate(-1)}
                                className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-semibold text-sm transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleSubmit} disabled={submitting || anyUploading || !canUpdateOrder || !allFieldsVerified || (paymentMethod === 'COUPON' && !canApplyCouponPerm) || utrDuplicate || (paymentMethod === 'COINS_REDEEM' && (availableCoins ?? 0) <= 0) || (paymentMethod === 'WALLET' && (availableWallet ?? 0) <= 0)}
                                title={!canUpdateOrder ? 'You do not have permission to update this order' : !allFieldsVerified ? 'Verify every field before adding this payment' : (paymentMethod === 'COUPON' && !canApplyCouponPerm) ? 'You do not have permission to apply coupons' : (utrDuplicate ? 'This transaction number already exists' : (paymentMethod === 'COINS_REDEEM' && (availableCoins ?? 0) <= 0) ? 'No coins available to redeem' : (paymentMethod === 'WALLET' && (availableWallet ?? 0) <= 0) ? 'No wallet balance available to redeem' : undefined)}
                                className="flex-1 px-4 py-3 bg-[#1B4332] hover:bg-[#163829] text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 shadow-sm active:scale-[0.98]">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Adding...</span>
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-4 h-4" />
                                        <span>Add Payment</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    ))}
                </>

            )}

            {/* Plain proof-image viewer for "Previous Payments" thumbnails — the active
                payment method's own proof image is shown inline above, not through this popup. */}
            <ImagePreviewModal
                url={previewUrl?.url ?? null}
                onClose={() => setPreviewUrl(null)}
            />
            {snackbar && <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />}

            {editingTx && orderId && (
                <EditTransactionModal
                    orderId={orderId}
                    transaction={editingTx}
                    isEmployee={!isAdmin && !isSuperAdmin && ['PENDING_SUBMISSION', 'PENDING_PAYMENT'].includes(order?.paymentStatus) && canEditOrderUsers}
                    userMobile={mobile}
                    maxAmount={remaining + Number((editingTx?.transaction?.amount ?? editingTx?.amount) || 0)}
                    onClose={() => setEditingTx(null)}
                    onSuccess={() => { setEditingTx(null); fetchOrder(); }}
                />
            )}

        </div>
    );
};

export default UserAddPaymentPage;
